import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "client";
import { ApiResponse } from "constantVariables";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "utils/axios";
import { decrypt } from "utils/crypto";
import { showToast } from "utils/toast";

export const getQuestionnaire = async (id) => {
  return await axiosInstance
    .get(`/questionnaire/${id}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const getDemoQuestionnaire = async (id) => {
  return await axiosInstance
    .get(`/questionnaire/demo/${id}`)
    .then((response) => {
      if (response.data.statusCode === ApiResponse.SUCCESS) {
        return decrypt(response.data.data);
      }
      return response.data.data;
    })
    .catch((error) => {
      throw error;
    });
};

export const getUnsolvedQuestionnaires = async () => {
  return await axiosInstance
    .get(`solvedquestionnair/getUnsolvedQuestionnaires`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useUnsolvedQuestionnaires = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["unsolvedQuestionnaires"],
    queryFn: getUnsolvedQuestionnaires,
  });

  const [questionnaires, setQuestionnaires] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setQuestionnaires(decrypt(data.data));
    }
  }, [data]);

  return { data: questionnaires, isLoading, error };
};

export const useDemoQuestionnaire = (id) => {
  return useQuery({
    queryKey: ["questionnaireDemo", id],
    queryFn: () => getDemoQuestionnaire(id),
  });
};

export const useQuestionnaire = (id) => {
  return useQuery({
    queryKey: ["questionnaire", id],
    queryFn: () => getQuestionnaire(id),
  });
};

export const startQuiz = async (id) => {
  return await axiosInstance
    .post(`/solvedquestionnair/${id}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useStartQuiz = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: startQuiz,
    onSuccess: (data) => {
      if (data.statusCode === ApiResponse.SUCCESS) {
        navigate(`/personal-ai/quiz/${data.data._id}`);
      } else {
        showToast(data.message, "error");
      }
    },
  });
};

export const getSolvedQuestionnaire = async (id) => {
  return await axiosInstance
    .get(`/solvedquestionnair/getQuiz/${id}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useQuiz = (id) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["quiz", id],
    queryFn: () => getSolvedQuestionnaire(id),
  });

  const [quiz, setQuiz] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setQuiz(decrypt(data.data));
    }
  }, [data]);

  return { data: quiz, isLoading, error };
};

export const addAnswer = async (answer) => {
  return await axiosInstance
    .post(`/solvedquestionnair/addAnswer`, answer)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useAddAnswer = () => {
  return useMutation({
    mutationFn: addAnswer,
  });
};

export const getNextQuestions = async (id) => {
  return await axiosInstance
    .post(`/solvedquestionnair/getNextQuestions/${id}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useNextQuestions = () => {
  return useMutation({
    mutationFn: getNextQuestions,
    onSuccess: (data) => {
      if (data.statusCode === ApiResponse.SUCCESS) {
        queryClient.invalidateQueries(["quiz", data.data._id]);
      } else {
        showToast(data.message, "error");
      }
    },
    onError: (error) => {
      showToast(error.message, "error");
    },
  });
};

export const submitQuiz = async (id) => {
  return await axiosInstance
    .post(`/solvedquestionnair/submit/${id}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useSubmitQuiz = () => {
  return useMutation({
    mutationFn: submitQuiz,
    onSuccess: (data) => {
      if (data.statusCode === ApiResponse.SUCCESS) {
        queryClient.invalidateQueries(["quiz", data.data._id]);
      } else {
        showToast(data.message, "error");
      }
    },
  });
};

export const getsolvedquestionnair = async () => {
  return await axiosInstance
    .get(`/solvedquestionnair`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useSolvedQuestionnaires = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["solvedquestionnair"],
    queryFn: getsolvedquestionnair,
  });

  const [questionnaires, setQuestionnaires] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setQuestionnaires(decrypt(data.data));
    }
  }, [data]);

  return { data: questionnaires, isLoading, error };
};

export const getAllQuestionnaire = async () => {
  return await axiosInstance
    .get("/questionnaire/all")
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
};

export const useAllQuestionnaire = (id) => {
  return useQuery({
    queryKey: ["allQuestionnaire"],
    queryFn: getAllQuestionnaire,
  });
};
